import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | authed | guest
    const [error, setError] = useState("");

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem("botmoileqi_token");
        if (!token) {
            setStatus("guest");
            setUser(null);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            setStatus("authed");
        } catch (e) {
            localStorage.removeItem("botmoileqi_token");
            setUser(null);
            setStatus("guest");
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("botmoileqi_token", data.token);
            setUser(data.user);
            setStatus("authed");
            return { ok: true };
        } catch (e) {
            const msg = formatApiError(e, "Connexion impossible.");
            setError(msg);
            return { ok: false, error: msg };
        }
    };

    // `pieges` : champs anti-robots du formulaire (usePiegeFormulaire).
    const register = async (email, password, name, pieges = {}) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", { email, password, name, ...pieges });
            localStorage.setItem("botmoileqi_token", data.token);
            setUser(data.user);
            setStatus("authed");
            return { ok: true };
        } catch (e) {
            const msg = formatApiError(e, "Inscription impossible.");
            setError(msg);
            return { ok: false, error: msg };
        }
    };

    const logout = () => {
        localStorage.removeItem("botmoileqi_token");
        setUser(null);
        setStatus("guest");
    };

    const loginWithToken = (token, authedUser) => {
        localStorage.setItem("botmoileqi_token", token);
        setUser(authedUser);
        setStatus("authed");
    };

    const updateUser = (patch) => setUser((u) => ({ ...u, ...patch }));

    const refresh = fetchMe;

    return (
        <AuthContext.Provider
            value={{ user, status, error, login, register, logout, loginWithToken, updateUser, refresh }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
